import {
    Alert,
    Anchor,
    Breadcrumbs,
    Button,
    Center,
    Drawer,
    Group,
    Loader,
    Pagination,
    Paper,
    Select,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { BaseLayout } from '@uag/react-core';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
    ActiveFilterChips,
    columnFilterChips,
    filterChipsWithPresets,
    type FilterChip,
} from './components/ActiveFilterChips';
import { ColumnPicker } from './components/ColumnPicker';
import { DetailOutlet } from './components/DetailOverlay';
import { DocumentFacetsPanel } from './components/DocumentFacetsPanel';
import { DocumentSearchResults } from './components/DocumentSearchResults';
import { FacetsPanel } from './components/FacetsPanel';
import { FILTER_DRAWER_HEADER_PROPS, FilterDrawerHeader } from './components/FilterDrawerHeader';
import { FitToViewport } from './components/FitToViewport';
import { HeaderBrand } from './components/HeaderBrand';
import { HeaderWishlist } from './components/HeaderWishlist';
import { LanguageSwitch } from './components/LanguageSwitch';
import { MobileHeaderBar } from './components/MobileHeaderBar';
import { NavigationTree } from './components/NavigationTree';
import { ProductGrid } from './components/ProductGrid';
import { ResizableAside } from './components/ResizableAside';
import { SearchShareActions } from './components/SearchShareActions';
import { SystemDetailContent } from './components/SystemDetailContent';
import { Toolbar } from './components/Toolbar';
import { TreeSearchHits } from './components/TreeSearchHits';
import {
    applyFilters,
    breadcrumbForNodeId,
    buildCategoryLabels,
    buildDocumentUsageIndex,
    buildIndexByArtNr,
    buildTreeIndex,
    byKey,
    categoryCounts,
    computeDocumentFacets,
    documentCategoryCounts,
    humanizeDocType,
    inAnyCategory,
    inAnyDocumentCategory,
    isSystemLeaf,
    languageLabel,
    searchIndex,
    searchTree,
    selectCatalog,
    selectDocuments,
    suggestQuery,
    type CatalogQueryState,
    type DocumentSortKey,
    type DocumentSortState,
} from './engine';
import { useCatalogLabels } from './labels';
import { DATA_LANGUAGE, useCatalogConfig, useCatalogIndex, useDocumentIndex, useLabels, useTree } from './queries';
import { fromSearchParams, sanitizeSearchState, isShareParam, useCatalogSearchState, type CatalogSearchState } from './searchShare';
import {
    columnFiltersAtom,
    documentDocTypesAtom,
    documentEditionsAtom,
    documentLanguageAtom,
    filtersAtom,
    listColumnsAtom,
    pageAtom,
    perPageAtom,
    scopeNodeIdsAtom,
    searchAtom,
    searchModeAtom,
    selectedDocumentNodeIdAtom,
    selectedNodeIdAtom,
    sortAtom,
    viewModeAtom,
} from './state';
import { loc, nodeLabel, type ActiveFilters, type ListColumn } from './types';

// Starts at 100 (see perPageAtom) per Fachbereich feedback - a full catalog page previously
// defaulted to 12/24/48, too few to browse comfortably at scale.
const PER_PAGE_OPTIONS = ['50', '100', '200', '500'];

export const CatalogPage = () => {
    const { locale, L } = useCatalogLabels();
    const [searchParams, setSearchParams] = useSearchParams();
    const presetKey = searchParams.get('preset');
    const [search, setSearch] = useAtom(searchAtom);
    const [searchMode, setSearchMode] = useAtom(searchModeAtom);
    const [selectedNodeId, setSelectedNodeId] = useAtom(selectedNodeIdAtom);
    const [selectedDocumentNodeId, setSelectedDocumentNodeId] = useAtom(selectedDocumentNodeIdAtom);
    const [filters, setFilters] = useAtom(filtersAtom);
    const [scopeNodeIds, setScopeNodeIds] = useAtom(scopeNodeIdsAtom);
    const [sort, setSort] = useAtom(sortAtom);
    const [viewMode, setViewMode] = useAtom(viewModeAtom);
    const [page, setPage] = useAtom(pageAtom);
    const [perPage, setPerPage] = useAtom(perPageAtom);
    const [listColumns, setListColumns] = useAtom(listColumnsAtom);
    const [columnFilters, setColumnFilters] = useAtom(columnFiltersAtom);
    // Document-search-only filters - global atoms (like selectedDocumentNodeId above) so
    // SearchMask's own advanced filters (landing page) can set them before navigating here.
    const [documentLanguage, setDocumentLanguage] = useAtom(documentLanguageAtom);
    const [documentDocTypes, setDocumentDocTypes] = useAtom(documentDocTypesAtom);
    const [documentEditions, setDocumentEditions] = useAtom(documentEditionsAtom);
    // Facets moved out of the fixed left column into an on-demand drawer (was cramped alongside
    // the category tree, see chat) - local UI state, no reason to persist/share it.
    const [facetsOpen, setFacetsOpen] = useState(false);
    // Category tree: permanent left sidebar from md upward, a Burger-triggered drawer below that
    // (was previously always rendered inline, pushing the whole results area far down the page on
    // narrow viewports - see chat screenshot).
    const [navOpen, { open: openNav, close: closeNav }] = useDisclosure(false);
    // Column sort of the document list (title ascending by default, see engine.sortDocumentGroups).
    // Local state - unlike the product list's sort, nothing outside this page needs it.
    const [documentSort, setDocumentSort] = useState<DocumentSortState>({ key: 'title', dir: 'asc' });
    const handleDocumentSort = (key: DocumentSortKey) =>
        setDocumentSort((prev) =>
            prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
        );

    const indexQuery = useCatalogIndex(DATA_LANGUAGE);
    const configQuery = useCatalogConfig();
    const treeQuery = useTree();
    const documentIndexQuery = useDocumentIndex();
    const labelsQuery = useLabels(locale);

    const fullIndex = useMemo(() => indexQuery.data ?? [], [indexQuery.data]);
    const config = configQuery.data;
    // Deep link (?preset=<key>, e.g. a bookmarked "Weltweites Verkaufsprogramm"): pre-applies the
    // preset's filter set once on arrival (see the effect below). The facets it sets stay fully
    // visible and editable in the drawer - the landing page's own entry (SearchMask) no longer
    // routes through this parameter but writes the same filters into filtersAtom directly.
    const preset = useMemo(() => config?.presets?.find((p) => p.key === presetKey), [config, presetKey]);
    const activeFilterCount = useMemo(() => Object.keys(filters).length, [filters]);
    const fullDocuments = useMemo(() => documentIndexQuery.data ?? [], [documentIndexQuery.data]);
    const documentFilterCount = (documentLanguage ? 1 : 0) + documentDocTypes.length + documentEditions.length;
    const labels = useMemo(
        () => labelsQuery.data ?? { language: locale, attributes: {}, lovs: {} },
        [labelsQuery.data, locale]
    );

    const treeIndex = useMemo(() => buildTreeIndex(treeQuery.data ?? []), [treeQuery.data]);
    // The landing page's multi-select scope (see state.scopeNodeIdsAtom) is applied HERE, once, to
    // both indexes - everything below (tree counts, facets, search, documents, breadcrumb labels)
    // then works on the cut-down catalog without knowing about it. Articles are scoped by their
    // own nodeIds; documents by the article they belong to or the node they target, so the
    // article lookup for that has to come from the FULL index (a document's article is what
    // decides whether the document is in scope).
    const index = useMemo(
        () => (scopeNodeIds.length > 0 ? fullIndex.filter((item) => inAnyCategory(item, scopeNodeIds, treeIndex)) : fullIndex),
        [fullIndex, scopeNodeIds, treeIndex]
    );
    const fullIndexByArtNr = useMemo(() => buildIndexByArtNr(fullIndex), [fullIndex]);
    const documents = useMemo(
        () =>
            scopeNodeIds.length > 0
                ? fullDocuments.filter((doc) => inAnyDocumentCategory(doc, scopeNodeIds, treeIndex, fullIndexByArtNr))
                : fullDocuments,
        [fullDocuments, scopeNodeIds, treeIndex, fullIndexByArtNr]
    );
    const indexByArtNr = useMemo(() => buildIndexByArtNr(index), [index]);
    const scopeLabels = useMemo(
        () => scopeNodeIds.map((nodeId) => ({ nodeId, label: nodeLabel(treeIndex.get(nodeId)?.node, locale) })),
        [scopeNodeIds, treeIndex, locale]
    );
    // Only a true system LEAF (see engine.isSystemLeaf) has its own Systeminformationen/Dokumente
    // view (SystemDetailContent, rendered inline below instead of the grid) - portfolio/group/
    // family selections (and a mistagged non-leaf 'system' node, should the data ever have one)
    // keep today's in-place grid filtering.
    const searching = search.trim().length >= (config?.search.minChars ?? 2);
    const selectedNode = selectedNodeId ? treeIndex.get(selectedNodeId)?.node : undefined;
    // A selected system leaf shows its own Systeminformationen/Artikelliste/Dokumente view - while
    // browsing. A search always covers the whole catalog (the former Suchbereich switch between
    // "this node" and "Ganzer Katalog" confused more than it helped, reported), so a running search
    // shows its catalog-wide hits in the grid instead; clearing the term brings the system back.
    const selectedSystemNode = selectedNode && isSystemLeaf(selectedNode) && !searching ? selectedNode : undefined;
    // Facet-filtered (but NOT category-filtered - the tree needs a count per node) product set,
    // so the tree badges match what the grid actually shows under the currently active filters
    // (including ones the landing page pre-applied, e.g. "Weltweites Sortiment").
    // Previously this counted the whole unfiltered catalog, so a node could show e.g. "1" while
    // selecting it yielded "0 Treffer" because that one product didn't match the active filters.
    const facetFilteredProducts = useMemo(() => {
        const products = index.filter((i) => i.type === 'product');
        return config ? applyFilters(products, filters, byKey(config.facets)) : products;
    }, [index, filters, config]);
    // The ranked hits of a running search, computed once here and handed to selectCatalog (and to
    // the "Meinten Sie" replay) - unfiltered: selectCatalog needs the raw hits to compute the
    // facets the drawer offers during a search.
    const searchHits = useMemo(
        () => (config && searching ? searchIndex(index, config, search) : undefined),
        [index, config, search, searching]
    );
    // The tree is navigation: clicking a node ends a search and shows that node (see
    // handleSelectCategory), so its badges always count what BROWSING the node shows under the
    // active filters - not the hits of a search the click is about to leave.
    const counts = useMemo(() => categoryCounts(facetFilteredProducts, treeIndex), [facetFilteredProducts, treeIndex]);
    // Document-mode tree badges: same unit ("logical documents", language variants collapsed) and
    // same active filters/search as the result list below, so a badge always promises exactly the
    // "N Treffer" that selecting the node delivers - see engine.documentCategoryCounts.
    const documentCounts = useMemo(
        () =>
            // Without the search term, for the same reason as `counts` above: a click in the tree
            // leaves the search.
            documentCategoryCounts(documents, indexByArtNr, treeIndex, config?.search.minChars ?? 2, {
                search: '',
                language: documentLanguage ?? undefined,
                docTypes: documentDocTypes,
                editions: documentEditions,
            }),
        [documents, indexByArtNr, treeIndex, config, documentLanguage, documentDocTypes, documentEditions]
    );
    const documentUsageIndex = useMemo(
        () => buildDocumentUsageIndex(documents, indexByArtNr, treeIndex, locale),
        [documents, indexByArtNr, treeIndex, locale]
    );
    const categoryLabels = useMemo(
        () => buildCategoryLabels(index, treeIndex, locale),
        [index, treeIndex, locale]
    );
    const crumbs = useMemo(
        () => breadcrumbForNodeId(selectedNodeId, treeIndex, locale),
        [selectedNodeId, treeIndex, locale]
    );
    // The tree's own hits of the running search, shown below the article list - see
    // components/TreeSearchHits.tsx. Confined like the article hits: to the landing page's scope if
    // one is active. Nodes with no product under the active filters are dropped (`counts`).
    const treeHits = useMemo(
        () => (searching ? searchTree(treeIndex, search, locale, scopeNodeIds, counts) : undefined),
        [searching, treeIndex, search, locale, scopeNodeIds, counts]
    );
    // Name of the node being browsed - part of a new Suchprofil's suggested name.
    const activeNodeId = searchMode === 'documents' ? selectedDocumentNodeId : selectedNodeId;
    const activeNodeLabel = activeNodeId ? nodeLabel(treeIndex.get(activeNodeId)?.node, locale) : undefined;
    // Label of the phone's categories button: the node being browsed, else "Kategorien".
    const navigationLabel = !searching && activeNodeLabel ? activeNodeLabel : L.categories;
    // The list's whole query state in one object: `result` renders it, and the "Meinten Sie" check
    // below replays it with a corrected query - a suggestion has to fill THIS list (tree node,
    // column filters), not just the catalog.
    const catalogState = useMemo<CatalogQueryState>(
        () => ({
            selectedNodeId,
            search,
            filters,
            sort,
            page,
            perPage,
            columnFilters,
            treeIndex,
            categoryLabels,
            // Computed above for the navigation badges: the very same hits, so the list does not
            // run the (catalog-wide) search a second time.
            searchHits,
        }),
        [
            selectedNodeId,
            search,
            filters,
            sort,
            page,
            perPage,
            columnFilters,
            treeIndex,
            categoryLabels,
            searchHits,
        ]
    );
    const result = useMemo(
        () => (config ? selectCatalog(index, config, catalogState) : undefined),
        [index, config, catalogState]
    );

    // Facets currently offered in the drawer - also while a search runs: the filters narrow the
    // hits now (engine.filterSearchHits), so the drawer has to stay reachable to change them.
    const visibleFacets = useMemo(() => result?.facets ?? [], [result]);

    // The node a search actually looks in: the selected one, unless the user widened the scope
    // (only a running search can widen it - browsing stays inside the selection either way).
    const scopedDocumentNodeId = searching ? undefined : selectedDocumentNodeId;

    const documentResult = useMemo(
        () =>
            selectDocuments(documents, indexByArtNr, treeIndex, config?.search.minChars ?? 2, {
                search,
                language: documentLanguage ?? undefined,
                docTypes: documentDocTypes,
                editions: documentEditions,
                nodeId: scopedDocumentNodeId,
                sort: documentSort,
                locale,
                page,
                perPage,
            }),
        [
            documents,
            indexByArtNr,
            treeIndex,
            config,
            search,
            documentLanguage,
            documentDocTypes,
            documentEditions,
            scopedDocumentNodeId,
            documentSort,
            locale,
            page,
            perPage,
        ]
    );

    // Facet values + smart-grayed counts for the document-mode drawer (DocumentFacetsPanel):
    // value lists from the base set (search/node applied, facet filters not - so picking one
    // filter never removes another group's options), counts per group with that group's own
    // filter excluded. See engine.computeDocumentFacets and doka-pih principle 3.
    const documentFacets = useMemo(
        () =>
            computeDocumentFacets(
                documents,
                indexByArtNr,
                treeIndex,
                config?.search.minChars ?? 2,
                {
                    search,
                    language: documentLanguage ?? undefined,
                    docTypes: documentDocTypes,
                    editions: documentEditions,
                    nodeId: scopedDocumentNodeId,
                    page,
                    perPage,
                },
                locale
            ),
        [
            documents,
            indexByArtNr,
            treeIndex,
            config,
            search,
            documentLanguage,
            documentDocTypes,
            documentEditions,
            scopedDocumentNodeId,
            page,
            perPage,
            locale,
        ]
    );
    const hasDocumentFacets =
        documentFacets.languages.length > 1 ||
        documentFacets.docTypes.length > 1 ||
        documentFacets.editions.length > 1;

    const availableColumns: ListColumn[] = useMemo(
        () => (config?.facets ?? []).map((f) => ({ key: f.key, label: loc(f.label, locale, f.key), unit: f.unit })),
        [config, locale]
    );
    const extraColumns = useMemo(
        () => availableColumns.filter((c) => listColumns.includes(c.key)),
        [availableColumns, listColumns]
    );

    // "Meinten Sie ...?" for a product list that came up empty. Only where the grid actually shows
    // it (not in document mode, not inside a system's own view): the first call builds the typo
    // vocabulary, a pass over the whole catalog, which would be wasted on a suggestion nobody sees.
    // A candidate counts only if it fills THIS list, so it is replayed through selectCatalog.
    const suggestion = useMemo(() => {
        if (!config || searchMode !== 'products' || selectedSystemNode || !result?.searching || result.total > 0) {
            return undefined;
        }
        return suggestQuery(
            index,
            config,
            search,
            (candidate) =>
                selectCatalog(index, config, { ...catalogState, search: candidate, searchHits: undefined }).total
        );
    }, [config, searchMode, selectedSystemNode, result, index, search, catalogState]);

    const sortValue = `${sort.key}:${sort.dir}`;

    // Everything currently narrowing the list, as removable chips under the toolbar (see
    // components/ActiveFilterChips.tsx): the landing page's multi-select scope (both modes - it
    // narrows the search too), then the filters of the current mode. Before, the scope sat on a
    // row of its own above the breadcrumb and the filters were only a number on the filter icon.
    const filterChips = useMemo((): FilterChip[] => {
        const scopeChips = scopeLabels.map(
            (scope): FilterChip => ({
                key: `scope:${scope.nodeId}`,
                group: L.scopedTo,
                value: scope.label,
                onRemove: () => setScopeNodeIds((prev) => prev.filter((id) => id !== scope.nodeId)),
            })
        );
        if (searchMode === 'products' && selectedSystemNode) {
            // A system shows its own Artikelliste with its own chip row - the catalog filters that
            // also narrow it are listed THERE ("Aus dem Katalog"), next to what they hide, rather
            // than up here, out of sight of the list they act on (review point 2). The catalog
            // list's column filters do not act on the system's table at all.
            return scopeChips;
        }
        if (searchMode === 'products') {
            const columnChips = columnFilterChips(
                      columnFilters,
                      (key) =>
                          key === 'name'
                              ? L.name
                              : key === 'artNr'
                                ? L.articleNumber
                                : key === 'category'
                                  ? result?.searching
                                      ? L.category
                                      : L.system
                                  : (availableColumns.find((column) => column.key === key)?.label ?? key),
                      ['category'],
                      L,
                      setColumnFilters
                  );
            return [
                ...scopeChips,
                ...filterChipsWithPresets(
                    filters,
                    config?.facets ?? [],
                    config?.presets ?? [],
                    labels,
                    locale,
                    L,
                    setFilters
                ),
                ...columnChips,
            ];
        }
        return [
            ...scopeChips,
            ...(documentLanguage
                ? [
                      {
                          key: 'doc:language',
                          group: L.documentLanguage,
                          value: languageLabel(documentLanguage, locale),
                          onRemove: () => setDocumentLanguage(null),
                      },
                  ]
                : []),
            ...documentDocTypes.map(
                (docType): FilterChip => ({
                    key: `doc:docType:${docType}`,
                    group: L.documentCategory,
                    value: humanizeDocType(docType),
                    onRemove: () => setDocumentDocTypes((prev) => prev.filter((value) => value !== docType)),
                })
            ),
            ...documentEditions.map(
                (edition): FilterChip => ({
                    key: `doc:edition:${edition}`,
                    group: L.documentEdition,
                    value: edition,
                    onRemove: () => setDocumentEditions((prev) => prev.filter((value) => value !== edition)),
                })
            ),
        ];
    }, [
        scopeLabels,
        searchMode,
        filters,
        config,
        labels,
        locale,
        L,
        documentLanguage,
        documentDocTypes,
        documentEditions,
        setScopeNodeIds,
        setFilters,
        setDocumentLanguage,
        setDocumentDocTypes,
        setDocumentEditions,
        selectedSystemNode,
        columnFilters,
        setColumnFilters,
        result?.searching,
        availableColumns,
    ]);
    const clearAllFilterChips = () => {
        setScopeNodeIds([]);
        if (searchMode === 'products') {
            setFilters({});
            setColumnFilters({});
        } else {
            setDocumentLanguage(null);
            setDocumentDocTypes([]);
            setDocumentEditions([]);
        }
    };

    // Applied once per distinct preset key, not on every render, so it doesn't clobber filters
    // the user has since changed or added on top.
    // A shared link (see searchShare.ts / SearchShareActions): its parameters describe the whole
    // search and are applied once the config and tree are there to validate them against - then
    // removed from the address bar, which would otherwise keep showing a search the user has since
    // changed (and re-apply it on reload). The ?preset= deep link below is left alone.
    const { apply: applySearchState } = useCatalogSearchState();
    const sanitize = useMemo(
        () =>
            (state: CatalogSearchState): CatalogSearchState =>
                config ? sanitizeSearchState(state, config, (nodeId) => treeIndex.has(nodeId)) : state,
        [config, treeIndex]
    );
    const sharedState = useMemo(() => fromSearchParams(searchParams), [searchParams]);
    useEffect(() => {
        if (!sharedState || !config || !treeQuery.data) {
            return;
        }
        applySearchState(sanitize(sharedState));
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                [...next.keys()].filter(isShareParam).forEach((name) => next.delete(name));
                return next;
            },
            { replace: true }
        );
    }, [sharedState, config, treeQuery.data, applySearchState, sanitize, setSearchParams]);

    // Pre-filled name of a new Suchprofil: term, node, number of filters - whatever is set.
    const defaultProfileName =
        [
            searchMode === 'documents' && (search.trim() || activeNodeLabel || filterChips.length > 0)
                ? L.searchModeDocuments
                : undefined,
            search.trim() ? (locale === 'en' ? `"${search.trim()}"` : `„${search.trim()}“`) : undefined,
            // The node only while browsing it - a search ignores it.
            searching ? undefined : activeNodeLabel,
            filterChips.length > 0 ? `${filterChips.length} ${L.filters}` : undefined,
        ]
            .filter(Boolean)
            .join(' · ') || (searchMode === 'documents' ? L.searchModeDocuments : L.allProducts);

    const appliedPresetKeyRef = useRef<string | null>(null);
    useEffect(() => {
        if (preset && appliedPresetKeyRef.current !== preset.key) {
            appliedPresetKeyRef.current = preset.key;
            setFilters((prev) => ({ ...prev, ...preset.filters }));
            // A deep link is a fresh entry: a landing-page scope from an earlier visit must not
            // sit on top of it (see state.scopeNodeIdsAtom).
            setScopeNodeIds([]);
        }
    }, [preset, setFilters, setScopeNodeIds]);

    // Closes the filter drawer the moment its own content would be empty (e.g. a free-text search
    // clears product filters mid-drawer, or the user flips searchMode while a low-variety document
    // set has no language/docType facets) - otherwise it stays open showing a blank panel, since
    // opening/closing is a one-shot user action with no other trigger to recheck this.
    useEffect(() => {
        if (facetsOpen && (searchMode === 'documents' ? !hasDocumentFacets : visibleFacets.length === 0)) {
            setFacetsOpen(false);
        }
    }, [facetsOpen, searchMode, hasDocumentFacets, visibleFacets]);

    const filtersKey = JSON.stringify(filters);
    const columnFiltersKey = JSON.stringify(columnFilters);
    const documentDocTypesKey = JSON.stringify(documentDocTypes);
    const documentEditionsKey = JSON.stringify(documentEditions);
    useEffect(() => {
        setPage(1);
    }, [
        setPage,
        selectedNodeId,
        search,
        filtersKey,
        columnFiltersKey,
        sortValue,
        perPage,
        searchMode,
        documentLanguage,
        documentDocTypesKey,
        documentEditionsKey,
        selectedDocumentNodeId,
    ]);

    // A free-text search keeps the facet filters - they narrow the hits (engine.filterSearchHits)
    // and stay visible as removable chips under the toolbar. It used to clear them (the old
    // publixx rule), which read as filters randomly disappearing (reported). The list's column
    // filters stay as well - they are chips now too, and a filter that silently vanishes on typing
    // was exactly the complaint. The search always covers the whole catalog; the tree selection
    // stays where it was and comes back into play once the term is cleared.
    const handleSearch = (value: string) => setSearch(value);

    const handleSelectCategory = (nodeId?: string) => {
        // A true system leaf (see engine.isSystemLeaf) renders its own Systeminformationen/
        // Artikelliste/Dokumente view (SystemDetailContent) right here in the content pane
        // instead of the grid (see selectedSystemNode below) - no navigation needed, the tree
        // sidebar simply stays as-is either way.
        setSelectedNodeId(nodeId);
        // Column filters survive a node change like every other catalog filter (review point 5:
        // one rule - navigating never drops a filter; only a system has filters of its own, which
        // go with it). They are chips, so a filter that no longer fits is visible and one click
        // away.
        // A click in the tree is navigation, and a search ignores the tree - so the click ends a
        // running search and shows the node (otherwise it would seem to do nothing at all while
        // the catalog-wide hits stay on screen).
        setSearch('');
    };

    const handleSelectDocumentNode = (nodeId?: string) => {
        setSelectedDocumentNodeId(nodeId);
        setSearch('');
    };

    const handleFilters = (next: ActiveFilters) => setFilters(next);

    // Clicking a column header toggles its direction on repeat clicks (see ProductGrid's
    // ColumnHeader); switching to a different column always starts ascending.
    const handleColumnSort = (key: string) => {
        setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
        setPage(1);
    };

    const loading =
        indexQuery.isLoading ||
        configQuery.isLoading ||
        treeQuery.isLoading ||
        (searchMode === 'documents' && documentIndexQuery.isLoading);
    const error =
        indexQuery.error ?? configQuery.error ?? treeQuery.error ?? (searchMode === 'documents' ? documentIndexQuery.error : null);
    const pageCount =
        searchMode === 'documents'
            ? Math.ceil(documentResult.total / perPage)
            : result
              ? Math.ceil(result.total / perPage)
              : 0;
    const totalForDisplay = searchMode === 'documents' ? documentResult.total : (result?.total ?? 0);

    return (
        <BaseLayout versionNumber={APP_VERSION}>
            <LanguageSwitch />
            <HeaderWishlist />
            <HeaderBrand />
            <MobileHeaderBar showTitle />
            <FitToViewport className="flex flex-col gap-4">
                {/* No padding of its own: Toolbar brings its own - a tab row plus one control
                    row, each padded to suit. `overflow: hidden` keeps the tab row's underline from
                    painting over the Paper's rounded corners. */}
                <Paper radius="md" shadow="xs" styles={{ root: { backgroundColor: '#fff', overflow: 'hidden' } }} withBorder>
                    <Toolbar
                        actions={<SearchShareActions defaultProfileName={defaultProfileName} sanitize={sanitize} />}
                        columns={
                            // Only where the grid below is actually a sortable LIST this toolbar
                            // governs: not in tile view (no columns), not in document mode (its own
                            // fixed columns) and not while a System leaf shows its own view (that
                            // one carries its own picker, scoped to its own Artikelliste).
                            searchMode === 'products' && !selectedSystemNode && viewMode === 'list' ? (
                                <ColumnPicker
                                    available={availableColumns}
                                    value={listColumns}
                                    onChange={setListColumns}
                                />
                            ) : undefined
                        }
                        filterChips={
                            filterChips.length > 0 ? (
                                <ActiveFilterChips L={L} chips={filterChips} onClearAll={clearAllFilterChips} />
                            ) : undefined
                        }
                        filtersActive={searchMode === 'documents' ? documentFilterCount > 0 : activeFilterCount > 0}
                        hideViewMode={Boolean(selectedSystemNode)}
                        minChars={config?.search.minChars ?? 2}
                        mode={searchMode}
                        navigation={
                            // Below `md` the tree is a drawer; its trigger used to float as a row of
                            // its own between the toolbar and the list. It is labelled with where the
                            // user is (the node being browsed), so on a phone it doubles as the
                            // breadcrumb's last step - "Kategorien" at the top level and while a
                            // search runs (a search ignores the tree).
                            <div className="md:hidden">
                                <Button
                                    leftSection={
                                        <span className="material-symbols-outlined" style={{ fontSize: 16 }} aria-hidden>
                                            menu
                                        </span>
                                    }
                                    // Compact, so that it shares one line with the count and the
                                    // display controls on a phone.
                                    size="compact-sm"
                                    title={navigationLabel}
                                    variant="default"
                                    onClick={navOpen ? closeNav : openNav}
                                >
                                    <span
                                        style={{
                                            display: 'block',
                                            maxWidth: 180,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {navigationLabel}
                                    </span>
                                </Button>
                            </div>
                        }
                        // Undefined - not 0 - while a System leaf is shown: the pane below is then
                        // SystemDetailContent with its own tabs and its own counts, so any number
                        // here would describe a list the user cannot see.
                        resultCount={selectedSystemNode ? undefined : totalForDisplay}
                        search={search}
                        viewMode={viewMode}
                        onMode={setSearchMode}
                        onOpenFilters={
                            // Not while a System leaf shows its own view: its Artikelliste has its
                            // own, system-scoped filter right there, and two identical filter icons
                            // on one screen - one for the catalog, one for the system - were the
                            // confusing part (reported). The catalog filters still set stay visible
                            // and removable as chips under this toolbar.
                            selectedSystemNode && searchMode === 'products'
                                ? undefined
                                : searchMode === 'documents'
                                ? hasDocumentFacets
                                    ? () => setFacetsOpen(true)
                                    : undefined
                                : visibleFacets.length > 0
                                  ? () => setFacetsOpen(true)
                                  : undefined
                        }
                        onSearch={handleSearch}
                        onViewMode={setViewMode}
                    />
                </Paper>

                {error && <Alert color="red">{L.loadError}</Alert>}

                {loading ? (
                    <Center h={240}>
                        <Loader />
                    </Center>
                ) : searchMode === 'documents' ? (
                    <div className="flex flex-col gap-6 md:flex-row">
                        {/* Same Paper, same padding as the toolbar above and the panes beside it
                            (see design review, point 7): the tree used to float straight on the page
                            background while everything around it sat on a card, so its rows started
                            on a different vertical line than every other block on the page. */}
                        <ResizableAside>
                            <Paper
                                p="sm"
                                radius="md"
                                shadow="xs"
                                styles={{ root: { backgroundColor: '#fff' } }}
                                withBorder
                            >
                            <NavigationTree
                                counts={documentCounts}
                                locale={locale}
                                nodes={treeQuery.data ?? []}
                                selectedNodeId={selectedDocumentNodeId}
                                showTitle={false}
                                title={L.categories}
                                onSelect={handleSelectDocumentNode}
                            />
                            </Paper>
                        </ResizableAside>

                        <div className="min-w-0 flex-1">
                            <Stack gap="md">
                                <Title order={4}>{L.documentIndexTitle}</Title>
                                <DocumentSearchResults
                                    items={documentResult.items}
                                    locale={locale}
                                    sort={documentSort}
                                    usageIndex={documentUsageIndex}
                                    onSelectNode={handleSelectDocumentNode}
                                    onSort={handleDocumentSort}
                                />

                                {/* Paging only. The "N Treffer" that used to lead this row now sits
                                    in the toolbar, right beside the search controls that produced
                                    it - repeating it here said the same thing twice on one
                                    screen. */}
                                {totalForDisplay > perPage && (
                                    <Group justify="space-between" mt="sm" wrap="wrap">
                                        <Group gap="xs">
                                            <Text c="dimmed" size="sm">
                                                {L.perPage}
                                            </Text>
                                            <Select
                                                allowDeselect={false}
                                                aria-label={L.perPage}
                                                data={PER_PAGE_OPTIONS}
                                                size="sm"
                                                value={String(perPage)}
                                                w={90}
                                                onChange={(next) => next && setPerPage(Number(next))}
                                            />
                                        </Group>
                                        {pageCount > 1 && <Pagination total={pageCount} value={page} onChange={setPage} />}
                                    </Group>
                                )}
                            </Stack>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-6 md:flex-row">
                        {/* See the document branch above - same Paper, same reason. */}
                        <ResizableAside>
                            <Paper
                                p="sm"
                                radius="md"
                                shadow="xs"
                                styles={{ root: { backgroundColor: '#fff' } }}
                                withBorder
                            >
                            <NavigationTree
                                counts={counts}
                                locale={locale}
                                nodes={treeQuery.data ?? []}
                                selectedNodeId={selectedNodeId}
                                showTitle={false}
                                title={L.categories}
                                onSelect={handleSelectCategory}
                            />
                            </Paper>
                        </ResizableAside>

                        <div className="min-w-0 flex-1">
                            <Stack gap="md">
                                {/* Only below the top level: a lone "Alle Produkte" told nothing the
                                    page did not already show (reported: "seltsam platziert").
                                    Phone: one "‹ <parent>" step back instead of the full trail,
                                    which wrapped onto two lines - the categories button above
                                    already names the current node. Desktop: the full trail. */}
                                {!result?.searching && crumbs.length > 0 && (
                                    <div className="md:hidden">
                                        <Anchor
                                            className="inline-flex items-center gap-1"
                                            size="sm"
                                            onClick={() => handleSelectCategory(crumbs[crumbs.length - 2]?.nodeId)}
                                        >
                                            <span className="material-symbols-outlined" style={{ fontSize: 18 }} aria-hidden>
                                                chevron_left
                                            </span>
                                            {crumbs[crumbs.length - 2]?.label ?? L.allProducts}
                                        </Anchor>
                                    </div>
                                )}
                                {!result?.searching && crumbs.length > 0 && (
                                    <div className="hidden md:block">
                                    <Breadcrumbs separator="›">
                                        <Anchor size="sm" onClick={() => handleSelectCategory(undefined)}>
                                            {L.allProducts}
                                        </Anchor>
                                        {crumbs.map((crumb, i) =>
                                            i === crumbs.length - 1 ? (
                                                <Text key={crumb.nodeId} fw={500} size="sm">
                                                    {crumb.label}
                                                </Text>
                                            ) : (
                                                <Anchor
                                                    key={crumb.nodeId}
                                                    size="sm"
                                                    onClick={() => handleSelectCategory(crumb.nodeId)}
                                                >
                                                    {crumb.label}
                                                </Anchor>
                                            )
                                        )}
                                    </Breadcrumbs>
                                    </div>
                                )}

                                {selectedSystemNode ? (
                                    // A true system leaf (see engine.isSystemLeaf) - its own
                                    // Systeminformationen/Artikelliste/Dokumente view renders right
                                    // here in the content pane, same as the grid otherwise would, so
                                    // the tree sidebar stays visible instead of navigating away (see
                                    // chat: "Kann man das System so einbetten...").
                                    <SystemDetailContent
                                        nodeId={selectedSystemNode.nodeId}
                                        onNavigateToNode={handleSelectCategory}
                                    />
                                ) : (
                                    <>
                                        {/* The column picker used to sit here, on a row of its
                                            own above the grid. It now lives at the right end of the
                                            toolbar together with the view and filter controls - all
                                            three act on the same list, so they belong in one place
                                            (see design review, point 1). */}
                                        {result && result.items.length > 0 ? (
                                            <ProductGrid
                                                categoryDisplay={result.searching ? 'path' : 'leaf'}
                                                categoryLabels={categoryLabels}
                                                categoryOptions={result.categoryOptions}
                                                columnFilters={columnFilters}
                                                columns={extraColumns}
                                                items={result.items}
                                                sort={sort}
                                                viewMode={viewMode}
                                                onColumnFilter={(key, value) =>
                                                    setColumnFilters((prev) => ({ ...prev, [key]: value }))
                                                }
                                                onSort={handleColumnSort}
                                            />
                                        ) : (
                                            <Stack gap={4}>
                                                <Text c="dimmed">{L.noResults}</Text>
                                                {suggestion && (
                                                    <Text size="sm">
                                                        {L.didYouMean}{' '}
                                                        <Anchor onClick={() => handleSearch(suggestion)}>
                                                            {suggestion}
                                                        </Anchor>
                                                        ?
                                                    </Text>
                                                )}
                                            </Stack>
                                        )}

                                        {/* Paging only - see the document branch above for why the
                                            count moved into the toolbar. */}
                                        {totalForDisplay > perPage && (
                                            <Group justify="space-between" mt="sm" wrap="wrap">
                                                <Group gap="xs">
                                                    <Text c="dimmed" size="sm">
                                                        {L.perPage}
                                                    </Text>
                                                    <Select
                                                        allowDeselect={false}
                                                        aria-label={L.perPage}
                                                        data={PER_PAGE_OPTIONS}
                                                        size="sm"
                                                        value={String(perPage)}
                                                        w={90}
                                                        onChange={(next) => next && setPerPage(Number(next))}
                                                    />
                                                </Group>
                                                {pageCount > 1 && <Pagination total={pageCount} value={page} onChange={setPage} />}
                                            </Group>
                                        )}

                                        {/* Articles first, then the tree (customer decision). A
                                            click on a node is "go there": the search is cleared so
                                            the node's full article list shows, not just the hits. */}
                                        {result?.searching && treeHits && (
                                            <TreeSearchHits
                                                hits={treeHits}
                                                onSelect={(nodeId) => {
                                                    setSearch('');
                                                    handleSelectCategory(nodeId);
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </Stack>
                        </div>
                    </div>
                )}
            </FitToViewport>

            <Drawer
                opened={facetsOpen}
                padding="lg"
                position="right"
                title={searchMode === 'documents' ? L.documentFilters : L.filters}
                withOverlay={false}
                onClose={() => setFacetsOpen(false)}
                {...FILTER_DRAWER_HEADER_PROPS}
            >
                {/* Own header row instead of the Drawer's `title` - see FilterDrawerHeader. */}
                <FilterDrawerHeader
                    canReset={searchMode === 'documents' ? documentFilterCount > 0 : activeFilterCount > 0}
                    title={searchMode === 'documents' ? L.documentFilters : L.filters}
                    onClose={() => setFacetsOpen(false)}
                    onReset={() => {
                        if (searchMode === 'documents') {
                            setDocumentLanguage(null);
                            setDocumentDocTypes([]);
                            setDocumentEditions([]);
                        } else {
                            handleFilters({});
                        }
                    }}
                />
                {searchMode === 'documents' ? (
                    <DocumentFacetsPanel
                        docTypes={documentFacets.docTypes}
                        editions={documentFacets.editions}
                        language={documentLanguage}
                        languages={documentFacets.languages}
                        locale={locale}
                        selectedDocTypes={documentDocTypes}
                        selectedEditions={documentEditions}
                        onDocTypesChange={setDocumentDocTypes}
                        onEditionsChange={setDocumentEditions}
                        onLanguageChange={setDocumentLanguage}
                    />
                ) : (
                    <FacetsPanel
                        facets={visibleFacets}
                        filters={filters}
                        labels={labels}
                        locale={locale}
                        onChange={handleFilters}
                    />
                )}
            </Drawer>

            <Drawer opened={navOpen} padding="lg" position="left" title={L.categories} onClose={closeNav}>
                <NavigationTree
                    counts={searchMode === 'documents' ? documentCounts : counts}
                    locale={locale}
                    nodes={treeQuery.data ?? []}
                    selectedNodeId={searchMode === 'documents' ? selectedDocumentNodeId : selectedNodeId}
                    showTitle={false}
                    title={L.categories}
                    onSelect={(nodeId) => {
                        (searchMode === 'documents' ? handleSelectDocumentNode : handleSelectCategory)(nodeId);
                        // Keep the drawer open while drilling down through Gruppe/Familie - only
                        // close once a leaf (System) node is picked, or "Alle Produkte" chosen
                        // (both terminal, nothing left to drill into) - otherwise every single
                        // level required reopening the drawer from scratch (see chat).
                        const node = nodeId ? treeIndex.get(nodeId)?.node : undefined;
                        if (!nodeId || (node && isSystemLeaf(node))) {
                            closeNav();
                        }
                    }}
                />
            </Drawer>

            {/* The detail routes (children of /catalog: article and document) render in here, as
                an overlay over this list - see components/DetailOverlay.tsx. */}
            <DetailOutlet fallbackPath="/catalog" />
        </BaseLayout>
    );
};
